import {
  Check,
  Bot,
  Cloud,
  Download,
  FolderOpen,
  KeyRound,
  Laptop,
  LoaderCircle,
  Moon,
  RefreshCw,
  History,
  Save,
  Settings2,
  ShieldCheck,
  Sun,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { DesktopClient } from "@orni/domain-typescript";
import type { AgentOperationKind } from "@orni/sdk-typescript";
import type { Snapshot } from "../domain";
import type {
  CommandReceipt,
  ConfigurableProviderId,
  PermissionMode,
  ProviderInventory,
  ProviderInventoryItem,
} from "../sdk/types";
import type { DesktopAccessPreferences } from "../desktop-access-preferences";
import { ProviderIcon } from "../components/ProviderIcon";
import { CommandDialog } from "../components/Ui";
import type { ThemeMode } from "../theme";
import { localizeError, useI18n } from "../i18n";
import { LanguageSelector } from "../components/LanguageSelector";
import { projectProviderInventoryToAgents, type DesktopAgentInventoryItem } from "../sdk/agent-facade";

export interface ProviderSettingsPageProps {
  snapshot: Snapshot;
  client: DesktopClient;
  inventory: ProviderInventory | null;
  onInventoryChange: (inventory: ProviderInventory) => void;
  onPickDirectory: () => Promise<string | null>;
  onSessionRootChange: (path: string) => void;
  theme: ThemeMode;
  onThemeChange: (theme: ThemeMode) => void;
  onManageApi?: (provider: ConfigurableProviderId) => void;
  /** Open the provider-scoped native history importer. */
  onOpenHistory?: (provider: ConfigurableProviderId) => void;
  sessionTransportMode: "local" | "remote";
  showSessionTransportSetting: boolean;
  onSessionTransportModeChange: (mode: "local" | "remote") => void;
  accessPreferences: DesktopAccessPreferences;
  onAccessPreferencesChange: (preferences: DesktopAccessPreferences) => Promise<void>;
  /** Render the existing provider inventory as the unified Agent management surface. */
  surface?: "settings" | "agents";
  /** Scope durable receipt pointers to the selected target computer. */
  operationStorageScope?: string;
  /** Render setup controls without writing to the real Daemon. */
  readOnly?: boolean;
}

export function ProviderSettingsPage({
  snapshot,
  client,
  inventory,
  onInventoryChange,
  onPickDirectory,
  onSessionRootChange,
  theme,
  onThemeChange,
  onManageApi,
  onOpenHistory,
  sessionTransportMode,
  showSessionTransportSetting,
  onSessionTransportModeChange,
  accessPreferences,
  onAccessPreferencesChange,
  surface = "settings",
  operationStorageScope = "local",
  readOnly = false,
}: ProviderSettingsPageProps) {
  const { t } = useI18n();
  const isAgentSurface = surface === "agents";
  const agentInventory = useMemo(
    () => inventory ? projectProviderInventoryToAgents(inventory) : null,
    [inventory],
  );
  const [sessionRoot, setSessionRoot] = useState(snapshot.preferences.sessionRoot);
  const [editing, setEditing] = useState<ConfigurableProviderId | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [remoteControlRoot, setRemoteControlRoot] = useState(
    accessPreferences.remoteControlRoot,
  );
  const [computerName, setComputerName] = useState(
    accessPreferences.computerName,
  );
  const [accessSaving, setAccessSaving] = useState(false);
  const [accessMessage, setAccessMessage] = useState("");
  const [providerCommand, setProviderCommand] = useState<{
    name: string;
    command: string;
    action: "install" | "upgrade";
  } | null>(null);
  useEffect(
    () => setSessionRoot(snapshot.preferences.sessionRoot),
    [snapshot.preferences.sessionRoot],
  );
  useEffect(() => {
    setRemoteControlRoot(accessPreferences.remoteControlRoot);
    setComputerName(accessPreferences.computerName);
  }, [accessPreferences]);
  const refresh = async () => {
    if (readOnly) return;
    setBusy(true);
    setMessage("");
    try {
      onInventoryChange(await client.refreshProviderInventory());
      setMessage(t("settings.provider.updated"));
    } catch (cause) {
      setMessage(localizeError(cause, t, "settings.provider.checkFailed"));
    } finally {
      setBusy(false);
    }
  };
  const chooseRoot = async () => {
    const path = await onPickDirectory();
    if (path) setSessionRoot(path);
  };
  const chooseRemoteControlRoot = async () => {
    const path = await onPickDirectory();
    if (path) setRemoteControlRoot(path);
  };
  const saveAccessPreferences = async (
    next: DesktopAccessPreferences,
  ) => {
    if (accessSaving) return;
    setAccessSaving(true);
    setAccessMessage("");
    try {
      await onAccessPreferencesChange(next);
      setAccessMessage(t("settings.access.saved"));
    } catch (cause) {
      setAccessMessage(localizeError(cause, t, "settings.access.saveFailed"));
    } finally {
      setAccessSaving(false);
    }
  };
  const saveRemoteControlRoot = () => {
    const path = remoteControlRoot.trim().replace(/\/$/, "");
    if (!path) return;
    setRemoteControlRoot(path);
    void saveAccessPreferences({ ...accessPreferences, remoteControlRoot: path });
  };
  const saveComputerName = () => {
    const value = computerName.trim();
    if (!value || value === accessPreferences.computerName) return;
    setComputerName(value);
    void saveAccessPreferences({ ...accessPreferences, computerName: value });
  };
  const saveDefaultPermission = (defaultPermissionMode: PermissionMode) => {
    if (defaultPermissionMode === accessPreferences.defaultPermissionMode) return;
    void saveAccessPreferences({
      ...accessPreferences,
      defaultPermissionMode,
    });
  };
  return (
    <div className={`settings-page provider-settings-page ${isAgentSurface ? "agent-management-page" : ""} page-scroll`}>
      <header className="list-page-header">
        <div><div className="page-heading-row"><Settings2 size={17} /><h1>{t(isAgentSurface ? "agents.title" : "settings.title")}</h1></div><p>{t(isAgentSurface ? "agents.lead" : "settings.lead")}</p></div>
      </header>
      <div className="settings-layout">
        {!isAgentSurface && showSessionTransportSetting ? (
          <section>
            <header><div><h2>{t("settings.transport.title")}</h2><p>{t("settings.transport.lead")}</p></div></header>
            <div className="transport-settings-row">
              <span>
                <strong>{t(`settings.transport.${sessionTransportMode}.title`)}</strong>
                <small>{t(`settings.transport.${sessionTransportMode}.detail`)}</small>
              </span>
              <div className="theme-choice" role="group" aria-label={t("settings.transport.title")}>
                <button className={sessionTransportMode === "local" ? "active" : ""} type="button" onClick={() => onSessionTransportModeChange("local")}><Laptop size={14} />{t("settings.transport.local.action")}</button>
                <button className={sessionTransportMode === "remote" ? "active" : ""} type="button" onClick={() => onSessionTransportModeChange("remote")}><Cloud size={14} />{t("settings.transport.remote.action")}</button>
              </div>
            </div>
          </section>
        ) : null}
        {!isAgentSurface && client.mode === "local" ? (
        <section className="desktop-access-settings">
          <header>
            <div>
              <h2>{t("settings.access.title")}</h2>
              <p>{t("settings.access.lead")}</p>
            </div>
          </header>
          <div className="desktop-access-row">
            <span>
              <strong>{t("settings.computerName.title")}</strong>
              <small>{t("settings.computerName.detail")}</small>
            </span>
            <input
              value={computerName}
              placeholder={t("settings.computerName.placeholder")}
              readOnly={readOnly}
              onChange={(event) => setComputerName(event.target.value)}
              onBlur={saveComputerName}
              onKeyDown={(event) => {
                if (event.key === "Enter") event.currentTarget.blur();
              }}
            />
          </div>
          <div className="desktop-access-row desktop-access-directory">
            <span>
              <strong>{t("settings.access.directory.title")}</strong>
              <small>{t("settings.access.directory.detail")}</small>
            </span>
            <div className="settings-root-row">
              <input
                value={remoteControlRoot}
                readOnly={readOnly}
                onChange={(event) => setRemoteControlRoot(event.target.value)}
              />
              <button type="button" disabled={readOnly} onClick={() => void chooseRemoteControlRoot()}>
                <FolderOpen size={14} />
                {t("common.choose")}
              </button>
              <button
                className="primary-button small"
                type="button"
                disabled={readOnly || accessSaving || !remoteControlRoot.trim()}
                onClick={saveRemoteControlRoot}
              >
                <Save size={13} />
                {t("common.save")}
              </button>
            </div>
          </div>
          <div className="desktop-access-row">
            <span>
              <strong>{t("settings.defaultPermission.title")}</strong>
              <small>{t("settings.defaultPermission.detail")}</small>
            </span>
            <div
              className="theme-choice desktop-permission-choice"
              role="group"
              aria-label={t("settings.defaultPermission.title")}
            >
              {(["read-only", "workspace-write", "full-access"] as const).map(
                (permission) => (
                  <button
                    className={
                      accessPreferences.defaultPermissionMode === permission
                        ? "active"
                        : ""
                    }
                    type="button"
                    disabled={readOnly || accessSaving}
                    key={permission}
                    onClick={() => saveDefaultPermission(permission)}
                  >
                    {t(
                      permission === "read-only"
                        ? "create.permission.readOnly"
                        : permission === "workspace-write"
                          ? "create.permission.workspaceWrite"
                          : "create.permission.fullAccess",
                    )}
                  </button>
                ),
              )}
            </div>
          </div>
          {accessMessage ? (
            <p className="settings-message" role="status">
              {accessMessage}
            </p>
          ) : null}
        </section>
        ) : null}
        {!isAgentSurface ? <section>
          <header><div><h2>{t("settings.appearance.title")}</h2><p>{t("settings.appearance.lead")}</p></div></header>
          <div className="theme-settings-row">
            <span><strong>{t("settings.theme.title")}</strong><small>{t("settings.theme.detail")}</small></span>
            <div className="theme-choice" role="group" aria-label={t("settings.theme.title")}>
              <button className={theme === "dark" ? "active" : ""} type="button" onClick={() => onThemeChange("dark")}><Moon size={14} />{t("settings.theme.dark")}</button>
              <button className={theme === "light" ? "active" : ""} type="button" onClick={() => onThemeChange("light")}><Sun size={14} />{t("settings.theme.light")}</button>
            </div>
          </div>
          <div className="language-settings-row">
            <span><strong>{t("settings.language.title")}</strong><small>{t("settings.language.detail")}</small></span>
            <LanguageSelector />
          </div>
        </section> : null}
        {isAgentSurface ? <section className="agent-provider-inventory">
          <header><div><h2>{t(isAgentSurface ? "agents.providers.title" : "common.provider")}</h2><p>{t(isAgentSurface ? "agents.providers.lead" : "settings.provider.lead")}</p></div><button className="ghost-button small" type="button" disabled={readOnly || busy} onClick={() => void refresh()}><RefreshCw className={busy ? "spin" : ""} size={13} />{t("settings.provider.refresh")}</button></header>
          <div className="provider-settings-list">
            {inventory?.providers.map((provider) => (
              <ProviderSettingsRow
                key={provider.projectedProviderId ?? provider.providerId}
                provider={provider}
                client={client}
                open={provider.legacyProviderId !== null && editing === provider.providerId}
                onToggle={() => {
                  if (provider.legacyProviderId === null) return;
                  setEditing(editing === provider.providerId ? null : provider.providerId);
                }}
                onInventoryChange={onInventoryChange}
                onManageApi={onManageApi ? () => onManageApi(provider.providerId) : undefined}
                onOpenHistory={onOpenHistory}
                onAgentOperation={readOnly ? undefined : async (operation) => {
                  const receipt = await client.agentOperation(provider.agentId ?? provider.providerId, operation, provider.revision);
                  // Lifecycle operations are durable and may outlive the click;
                  // refresh once immediately and let the caller poll naturally.
                  window.setTimeout(() => void refresh(), 500);
                  return receipt;
                }}
                onAgentOperationStatus={readOnly ? undefined : (commandId, operation, options) =>
                  client.waitForAgentOperation
                    ? client.waitForAgentOperation(commandId, operation, options)
                    : Promise.reject(new Error("Agent operation receipts are unavailable."))
                }
                onRefreshInventory={readOnly ? undefined : async () => {
                  onInventoryChange(await client.refreshProviderInventory());
                }}
                agent={agentInventory?.agents.find((item) =>
                  item.agentId === provider.agentId ||
                  item.providerId === provider.providerId ||
                  item.displayName === provider.displayName,
                )}
                onShowCommand={(action, command) =>
                  setProviderCommand({
                    name: provider.displayName,
                    command,
                    action,
                  })
                }
                operationStorageScope={operationStorageScope}
              />
            ))}
            {!inventory ? <div className="settings-loading"><LoaderCircle className="spin" size={16} />{t("settings.provider.loading")}</div> : null}
            {inventory && inventory.providers.length === 0 ? <div className="settings-empty-state"><Bot size={20} /><strong>{t("settings.provider.emptyInventory")}</strong><small>{t("settings.provider.emptyInventoryDetail")}</small></div> : null}
          </div>
          {message ? <p className="settings-message">{message}</p> : null}
        </section> : null}
        {!isAgentSurface ? <section>
          <header><div><h2>{t("settings.projectRoot.title")}</h2><p>{t("settings.projectRoot.lead")}</p></div></header>
          <div className="settings-root-row"><input value={sessionRoot} readOnly={readOnly} onChange={(event) => setSessionRoot(event.target.value)} /><button type="button" disabled={readOnly} onClick={() => void chooseRoot()}><FolderOpen size={14} />{t("common.choose")}</button><button className="primary-button small" type="button" disabled={readOnly} onClick={() => onSessionRootChange(sessionRoot.trim().replace(/\/$/, ""))}><Save size={13} />{t("common.save")}</button></div>
        </section> : null}
        {!isAgentSurface ? <section>
          <header><div><h2>{t("settings.service.title")}</h2><p>{t("settings.service.lead")}</p></div></header>
          <div className="settings-service-row"><span><strong>{snapshot.daemon.version}</strong><small>{t("settings.service.detail")}</small></span><b className={snapshot.daemon.state === "online" ? "setting-online" : "setting-offline"}>{snapshot.daemon.state === "online" ? <><Check size={13} />{t("settings.service.online")} · {snapshot.daemon.latency}ms</> : t("settings.service.offline")}</b></div>
          <div className="settings-service-row"><span><strong>{t("settings.credentials.title")}</strong><small>{t("settings.credentials.detail")}</small></span><b className="setting-online"><ShieldCheck size={13} />{t("settings.credentials.local")}</b></div>
        </section> : null}
      </div>
      {providerCommand ? (
        <CommandDialog
          title={`${providerCommand.action === "install" ? t("settings.command.install") : t("settings.command.upgrade")} ${providerCommand.name}`}
          description={t("settings.command.detail")}
          command={providerCommand.command}
          onClose={() => setProviderCommand(null)}
        />
      ) : null}
    </div>
  );
}

/** Unified Agent entry point. Kept as a thin facade so old settings deep links remain valid. */
export function AgentManagementPage(props: ProviderSettingsPageProps) {
  return <ProviderSettingsPage {...props} surface="agents" />;
}

function ProviderSettingsRow({
  provider,
  agent,
  client,
  open,
  onToggle,
  onInventoryChange,
  onShowCommand,
  onAgentOperation,
  onAgentOperationStatus,
  onRefreshInventory,
  onManageApi,
  onOpenHistory,
  operationStorageScope = "local",
}: {
  provider: ProviderInventoryItem;
  agent?: DesktopAgentInventoryItem;
  client: DesktopClient;
  open: boolean;
  onToggle: () => void;
  onInventoryChange: (inventory: ProviderInventory) => void;
  onShowCommand: (action: "install" | "upgrade", command: string) => void;
  onAgentOperation?: (operation: AgentOperationKind) => Promise<CommandReceipt>;
  onAgentOperationStatus?: (commandId: string, operation: AgentOperationKind, options?: { timeoutMs?: number; signal?: AbortSignal }) => Promise<CommandReceipt>;
  /** Reconcile the row with Daemon inventory after a terminal receipt. */
  onRefreshInventory?: () => Promise<void>;
  onManageApi?: () => void;
  onOpenHistory?: (provider: ConfigurableProviderId) => void;
  operationStorageScope?: string;
}) {
  const { t } = useI18n();
  const [enabled, setEnabled] = useState(provider.enabled);
  const [executablePath, setExecutablePath] = useState(provider.configuredExecutablePath ?? "");
  const [homeDirectory, setHomeDirectory] = useState(provider.configuredHomeDirectory ?? provider.homeDirectory);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [operationBusy, setOperationBusy] = useState<string | null>(null);
  const [operationReceipt, setOperationReceipt] = useState<CommandReceipt | null>(null);
  const [lastOperation, setLastOperation] = useState<AgentOperationKind | null>(null);
  const [observationCancelled, setObservationCancelled] = useState(false);
  const [observedCommandId, setObservedCommandId] = useState<string | null>(null);
  const operationAbort = useRef<AbortController | null>(null);
  // Keep the latest lifecycle command id in browser storage so a renderer
  // reload (or a temporary Cloud reconnect) does not lose the durable
  // operation receipt.  The Daemon remains the source of truth; this is only
  // a pointer used to rehydrate the observation UI.
  const operationStorageKey = `orni.agent-operation.${operationStorageScope}.${provider.agentId ?? provider.providerId}`;
  const restoredOperation = useRef(false);
  const configurable = provider.legacyProviderId !== null;
  useEffect(() => {
    setEnabled(provider.enabled);
    setExecutablePath(provider.configuredExecutablePath ?? "");
    setHomeDirectory(
      provider.configuredHomeDirectory ?? provider.homeDirectory,
    );
  }, [provider]);
  useEffect(() => {
    if (restoredOperation.current || !onAgentOperationStatus || !configurable) return;
    restoredOperation.current = true;
    let raw: string | null = null;
    try {
      raw = window.localStorage.getItem(operationStorageKey);
    } catch {
      // Restricted WebViews may disable storage; the in-memory UI still works.
    }
    if (!raw) return;
    let saved: { commandId?: string; operation?: AgentOperationKind };
    try {
      saved = JSON.parse(raw) as { commandId?: string; operation?: AgentOperationKind };
    } catch {
      return;
    }
    if (!saved.commandId || !saved.operation) return;
    const controller = new AbortController();
    operationAbort.current = controller;
    setLastOperation(saved.operation);
    setObservedCommandId(saved.commandId);
    setOperationBusy(saved.operation);
    setObservationCancelled(false);
    let unmounted = false;
    void onAgentOperationStatus(saved.commandId, saved.operation, {
      // A renderer restart resumes the same durable command automatically.
      // The command id is authoritative, so this cannot duplicate package
      // manager work even if the previous renderer disappeared mid-operation.
      timeoutMs: 300_000,
      signal: controller.signal,
    }).then((receipt) => {
      if (unmounted) return;
      setOperationReceipt(receipt);
      if (receipt.status !== "succeeded" && receipt.status !== "failed" && receipt.status !== "rejected") {
        // The bounded observer elapsed or the user stopped watching. The
        // command stays Daemon-owned and can be observed again by id.
        setObservationCancelled(true);
      }
      if (receipt.status === "failed" || receipt.status === "rejected") {
        setError(receipt.detail || t("settings.provider.checkFailed"));
      }
      if (receipt.status === "succeeded") {
        setEnabled(saved.operation === "install" || saved.operation === "upgrade" || saved.operation === "enable");
      }
      if (receipt.status === "succeeded" || receipt.status === "failed" || receipt.status === "rejected") {
        const refresh = onRefreshInventory?.();
        if (refresh) void refresh.catch(() => undefined);
      }
    }).catch((cause) => {
      if (!unmounted && !controller.signal.aborted) {
        setError(localizeError(cause, t, "settings.provider.checkFailed"));
        setObservationCancelled(true);
      }
    }).finally(() => {
      if (unmounted) return;
      if (operationAbort.current === controller) operationAbort.current = null;
      setOperationBusy(null);
    });
    return () => {
      unmounted = true;
      controller.abort();
      if (operationAbort.current === controller) operationAbort.current = null;
    };
  }, [configurable, onAgentOperationStatus, onRefreshInventory, operationStorageKey, t]);
  const save = async () => {
    setSaving(true);
    setError("");
    try {
      onInventoryChange(await client.updateProviderSettings(provider.providerId, {
        enabled,
        executablePath: executablePath.trim() || null,
        homeDirectory: homeDirectory.trim() || null,
        expectedRevision: provider.revision,
      }));
      onToggle();
    } catch (cause) {
      setError(localizeError(cause, t, "settings.provider.saveFailed"));
    } finally {
      setSaving(false);
    }
  };
  const observeOperation = async (
    commandId: string,
    operation: AgentOperationKind,
  ) => {
    if (!onAgentOperationStatus || (operationBusy && observedCommandId !== commandId)) return;
    const controller = new AbortController();
    operationAbort.current = controller;
    setOperationBusy(operation);
    setObservedCommandId(commandId);
    setObservationCancelled(false);
    try {
      const latest = await onAgentOperationStatus(commandId, operation, {
        timeoutMs: 300_000,
        signal: controller.signal,
      });
      setOperationReceipt(latest);
      // Stopping observation never cancels the Daemon command. Keep its
      // command id so the same durable receipt can be observed again.
      const terminal = latest.status === "succeeded" || latest.status === "failed" || latest.status === "rejected";
      if (controller.signal.aborted || !terminal) {
        setObservationCancelled(true);
      }
      if (latest.status === "failed" || latest.status === "rejected") {
        setError(latest.detail || t("settings.provider.checkFailed"));
      }
      if (latest.status === "succeeded") {
        setEnabled(operation === "install" || operation === "upgrade" || operation === "enable");
      }
      if (terminal) {
        try {
          await onRefreshInventory?.();
        } catch (cause) {
          setError(localizeError(cause, t, "settings.provider.checkFailed"));
        }
      }
    } catch (cause) {
      if (controller.signal.aborted) {
        setObservationCancelled(true);
      } else {
        setError(localizeError(cause, t, "settings.provider.checkFailed"));
      }
    } finally {
      if (operationAbort.current === controller) operationAbort.current = null;
      setOperationBusy(null);
    }
  };
  const runOperation = async (operation: AgentOperationKind) => {
    if (!onAgentOperation || operationBusy) return;
    const confirmationKey = operation === "install"
      ? "settings.provider.installConfirm"
      : operation === "upgrade"
        ? "settings.provider.upgradeConfirm"
        : operation === "uninstall"
          ? "settings.provider.uninstallConfirm"
          : operation === "enable"
            ? "settings.provider.enableConfirm"
            : "settings.provider.disableConfirm";
    if (!window.confirm(t(confirmationKey))) return;
    setOperationBusy(operation);
    setLastOperation(operation);
    setError("");
    setOperationReceipt(null);
    setObservationCancelled(false);
    setObservedCommandId(null);
    try {
      const receipt = await onAgentOperation(operation);
      setOperationReceipt(receipt);
      try {
        window.localStorage.setItem(
          operationStorageKey,
          JSON.stringify({ commandId: receipt.id, operation }),
        );
      } catch {
        // Storage is optional; durable receipt observation still works.
      }
      if (receipt.status === "failed" || receipt.status === "rejected") {
        setError(receipt.detail || t("settings.provider.checkFailed"));
        try {
          await onRefreshInventory?.();
        } catch (cause) {
          setError(localizeError(cause, t, "settings.provider.checkFailed"));
        }
      } else if (receipt.status !== "succeeded" && onAgentOperationStatus) {
        // Receipt acceptance is not completion. Observe the same command id;
        // never issue a second lifecycle command while the package manager is
        // still running on the Daemon.
        setObservedCommandId(receipt.id);
        await observeOperation(receipt.id, operation);
      } else {
        setEnabled(operation === "install" || operation === "upgrade" || operation === "enable");
        try {
          await onRefreshInventory?.();
        } catch (cause) {
          setError(localizeError(cause, t, "settings.provider.checkFailed"));
        }
      }
    }
    catch (cause) { setError(localizeError(cause, t, "settings.provider.checkFailed")); }
    finally { operationAbort.current = null; setOperationBusy(null); }
  };
  const receiptStatusLabel = (() => {
    if (!operationReceipt) return "";
    switch (operationReceipt.status) {
      case "accepted": return t("settings.agentReceipt.accepted");
      case "running": return t("settings.agentReceipt.running");
      case "succeeded": return t("settings.agentReceipt.succeeded");
      case "failed": return t("settings.agentReceipt.failed");
      case "rejected": return t("settings.agentReceipt.rejected");
    }
  })();
  return <article className={`provider-settings-row ${open ? "open" : ""}`}>
    <div className="provider-settings-summary">
      <i><ProviderIcon provider={provider.providerId} size={18} /></i>
      <button type="button" disabled={!configurable} onClick={onToggle}><strong>{provider.displayName}</strong><small>{provider.candidate ? t("agents.providers.candidate") : provider.installed ? `${provider.version ?? t("settings.provider.installed")} · ${provider.executablePath}` : t("settings.provider.notDetected")}</small></button>
      <span className={provider.candidate ? "adapter-state" : provider.installed && provider.enabled ? "adapter-state ready" : "adapter-state"}>{provider.candidate ? t("agents.providers.candidateStatus") : provider.installed ? provider.enabled ? t("settings.provider.enabled") : t("settings.provider.disabled") : t("settings.provider.notInstalled")}</span>
    </div>
    {open && configurable ? <div className="provider-settings-form">
      <label className="provider-enabled"><span>{t("settings.provider.enable")}</span><button type="button" className={`toggle ${enabled ? "on" : ""}`} disabled={operationBusy !== null || !onAgentOperation} onClick={() => void runOperation(provider.enabled ? "disable" : "enable")}><i /></button></label>
      <label><span>{t("settings.provider.executable")}</span><input value={executablePath} placeholder={provider.executablePath ?? t("settings.provider.executablePlaceholder")} onChange={(event) => setExecutablePath(event.target.value)} /></label>
      <label><span>{t("settings.provider.home")}</span><input value={homeDirectory} onChange={(event) => setHomeDirectory(event.target.value)} /></label>
      <div className="provider-settings-actions">{onManageApi ? <button type="button" onClick={onManageApi}><KeyRound size={13} />{t("settings.provider.manageApi")}</button> : null}{onOpenHistory && provider.legacyProviderId !== null ? <button type="button" onClick={() => onOpenHistory(provider.providerId)}><History size={13} />{t("agents.history.open")}</button> : null}{onAgentOperation ? <>{!provider.installed ? <button type="button" disabled={operationBusy !== null} onClick={() => void runOperation("install")}><Download size={13} />{operationBusy === "install" ? <LoaderCircle className="spin" size={13} /> : t("settings.command.install")}</button> : <><button type="button" disabled={operationBusy !== null} onClick={() => void runOperation("upgrade")}><Download size={13} />{operationBusy === "upgrade" ? <LoaderCircle className="spin" size={13} /> : t("settings.command.upgrade")}</button><button type="button" disabled={operationBusy !== null} onClick={() => void runOperation("uninstall")}><Download size={13} />{operationBusy === "uninstall" ? <LoaderCircle className="spin" size={13} /> : t("settings.command.uninstall")}</button></>}</> : <button type="button" onClick={() => onShowCommand(provider.installed ? "upgrade" : "install", provider.installed ? provider.upgradeCommand : provider.installCommand)}><Download size={13} />{provider.installed ? t("settings.command.upgrade") : t("settings.command.install")} {provider.displayName}</button>}<button className="primary-button small" type="button" disabled={saving || operationBusy !== null} onClick={() => void save()}>{saving ? <LoaderCircle className="spin" size={13} /> : <Save size={13} />}{t("common.save")}</button></div>
      {operationReceipt ? <p className={`agent-operation-receipt receipt-${operationReceipt.status}`} role="status">
        <strong>{receiptStatusLabel}</strong>
        {operationReceipt.detail ? <span>{operationReceipt.detail}</span> : null}
        {operationBusy && operationAbort.current ? <button type="button" onClick={() => operationAbort.current?.abort()}>{t("settings.agentReceipt.cancelObserve")}</button> : null}
        {!operationBusy && observationCancelled && observedCommandId && lastOperation ? <button type="button" onClick={() => void observeOperation(observedCommandId, lastOperation)}>{t("settings.agentReceipt.resumeObserve")}</button> : null}
        {!operationBusy && lastOperation && (operationReceipt.status === "failed" || operationReceipt.status === "rejected") ? <button type="button" onClick={() => void runOperation(lastOperation)}>{t("settings.agentReceipt.retry")}</button> : null}
      </p> : null}
      {error ? <p role="alert">{error}</p> : null}
    </div> : null}
  </article>;
}
